/**
 * Service for importing elements from JSON format
 * Handles validation, ID mapping, and batch import of nodes and edges
 */

/**
 * Import elements from JSON file
 * @param {Array} jsonElements - Array of elements to import
 * @param {Object} dependencies - Required dependencies
 * @param {Function} dependencies.addNodeData - Function to add a node/edge to Firestore
 * @param {Object} dependencies.projectData - Project data object with defaults
 * @param {Object} dependencies.elementsRef - React ref containing current elements
 * @param {Function} dependencies.setNotificationOpen - Function to show notifications
 * @param {Object} options - Import options
 * @param {boolean} options.preserveIds - If true, tries to preserve original IDs (may fail if IDs conflict)
 * @returns {Promise<Object>} Result object with success count, error count, and any errors
 */
export const importElementsFromJson = async (
    jsonElements,
    { addNodeData, projectData, elementsRef, setNotificationOpen },
    options = {}
) => {
    if (!Array.isArray(jsonElements)) {
        setNotificationOpen({ 
            state: true, 
            sev: "error", 
            msg: "Invalid JSON format: Expected an array of elements" 
        });
        return { success: 0, errors: 1, errorMessages: ["Invalid JSON format: Expected an array"] };
    }

    if (jsonElements.length === 0) {
        setNotificationOpen({ 
            state: true, 
            sev: "warning", 
            msg: "No elements to import" 
        });
        return { success: 0, errors: 0, errorMessages: [] };
    }

    const errors = [];
    let successCount = 0;
    const idMapping = {}; // Maps old IDs to new Firestore IDs

    try {
        // Separate nodes and edges
        const nodes = [];
        const edges = [];

        for (const element of jsonElements) {
            // Validate element structure
            if (!element.group || (element.group !== "nodes" && element.group !== "edges")) {
                errors.push(`Invalid element: missing or invalid 'group' property. Must be "nodes" or "edges"`);
                continue;
            }

            if (!element.data || typeof element.data !== "object") {
                errors.push(`Invalid element: missing or invalid 'data' property`);
                continue;
            }

            // Validate edges have source and target
            if (element.group === "edges") {
                if (!element.data.source || !element.data.target) {
                    errors.push(`Invalid edge: missing 'source' or 'target' in data`);
                    continue;
                }
            }

            if (element.group === "nodes") {
                nodes.push(element);
            } else {
                edges.push(element);
            }
        }

        // Import nodes first
        for (const node of nodes) {
            try {
                // Remove id if present (will be generated by Firestore)
                const nodeToImport = {
                    ...node,
                    data: { ...node.data }
                };
                
                const oldId = nodeToImport.data.id;
                delete nodeToImport.data.id;

                // Set default nodeshape if not provided
                if (!nodeToImport.data.nodeshape && projectData?.defaultshapetype) {
                    nodeToImport.data.nodeshape = projectData.defaultshapetype;
                }

                // Import the node
                const newId = await addNodeData(nodeToImport);
                
                // Map old ID to new ID for edge references
                if (oldId) {
                    idMapping[oldId] = newId;
                }

                successCount++;
            } catch (error) {
                const nodeName = node.data?.name || "Unknown";
                errors.push(`Failed to import node "${nodeName}": ${error.message}`);
                console.error("Error importing node:", error);
            }
        }

        // Import edges after nodes (so source/target references can be resolved)
        for (const edge of edges) {
            try {
                const edgeToImport = {
                    ...edge,
                    data: { ...edge.data }
                };

                // Remove id if present
                delete edgeToImport.data.id;

                // Map source and target IDs if they exist in mapping
                const oldSourceId = edgeToImport.data.source;
                const oldTargetId = edgeToImport.data.target;

                if (idMapping[oldSourceId]) {
                    edgeToImport.data.source = idMapping[oldSourceId];
                }
                if (idMapping[oldTargetId]) {
                    edgeToImport.data.target = idMapping[oldTargetId];
                }

                // Set default edge properties if not provided
                if (!edgeToImport.data.edgetype && projectData?.defaultedgetype) {
                    edgeToImport.data.edgetype = projectData.defaultedgetype;
                }
                if (!edgeToImport.data.edgearrows && projectData?.edgearrows) {
                    edgeToImport.data.edgearrows = projectData.edgearrows;
                }

                // Validate that source and target exist in current elements or were just imported
                const sourceExists = elementsRef.current.some(el => 
                    el.group === "nodes" && el.data.id === edgeToImport.data.source
                ) || Object.values(idMapping).includes(edgeToImport.data.source);
                const targetExists = elementsRef.current.some(el => 
                    el.group === "nodes" && el.data.id === edgeToImport.data.target
                ) || Object.values(idMapping).includes(edgeToImport.data.target);

                if (!sourceExists || !targetExists) {
                    errors.push(`Edge "${edge.data?.name || "Unknown"}" skipped: source or target node not found`);
                    continue;
                }

                // Import the edge
                await addNodeData(edgeToImport);
                successCount++;
            } catch (error) {
                const edgeName = edge.data?.name || "Unknown";
                errors.push(`Failed to import edge "${edgeName}": ${error.message}`);
                console.error("Error importing edge:", error);
            }
        }

        // Show success/error notification
        if (errors.length === 0) {
            setNotificationOpen({ 
                state: true, 
                sev: "success", 
                msg: `Successfully imported ${successCount} element(s)` 
            });
        } else if (successCount > 0) {
            setNotificationOpen({ 
                state: true, 
                sev: "warning", 
                msg: `Imported ${successCount} element(s) with ${errors.length} error(s)` 
            });
        } else {
            setNotificationOpen({ 
                state: true, 
                sev: "error", 
                msg: `Failed to import elements: ${errors.length} error(s)` 
            });
        }

        return {
            success: successCount,
            errors: errors.length,
            errorMessages: errors
        };
    } catch (error) {
        console.error("Error during import:", error);
        setNotificationOpen({ 
            state: true, 
            sev: "error", 
            msg: `Import failed: ${error.message}` 
        });
        return {
            success: successCount,
            errors: errors.length + 1,
            errorMessages: [...errors, error.message]
        };
    }
};

